import React, { useCallback, useEffect, useRef, useState } from "react";
import type {
  CardSaveOutcome,
  EmailDraft,
} from "../../../../features/card-to-crm/receptionistCardService";
import { MAX_CARDS_PER_SCAN as MAX_IMAGES_PER_EXTRACT } from "../../../../features/card-to-crm/receptionistCardService";
import { filesToCompressedDataUrls } from "../../../../features/card-to-crm/utils/imageToBase64";
import {
  Contact,
  ContactField,
} from "../../../../features/card-to-crm/types/cardToCrm.types";
import { ContactFieldsEditor } from "./ContactFieldsEditor";
import { captureRect } from "./cardCaptureRect";
import { useVoiceDictation } from "../../../../hooks/useVoiceDictation";

interface CardCapturePanelProps {
  isOpen: boolean;
  onClose: () => void;
  /** Called on close with one row per card that reached the CRM, so the thread
   *  keeps a record of the scan. Not called when nothing was saved. */
  onScanComplete: (rows: ScanReportRow[]) => void;
  /** Runs the Cognito-authorized extraction for this customer. */
  onExtract: (images: string[]) => Promise<Contact[]>;
  /** SGL-346: hand the extracted cards to the chat, which drafts the intro emails
   *  and renders them as a review card. When provided, the panel closes straight
   *  after extraction and the in-panel review below is never shown. */
  onHandOffToChat?: (contacts: Contact[]) => void | Promise<void>;
  /** Compose the intro emails without saving or sending, for review + editing. */
  onDraftIntros: (
    contacts: Contact[],
    captureContext: string
  ) => Promise<{ drafts: (EmailDraft | null)[]; suggestedTag: string | null }>;
  /** Save one card to GHL, sending the (possibly edited) intro if asked. */
  onSaveContact: (
    contact: Contact,
    captureContext: string,
    send: boolean,
    draft: EmailDraft | null,
    tags: string[]
  ) => Promise<CardSaveOutcome>;
  /** Save every still-unsaved card in one request. */
  onSaveAll: (
    contacts: Contact[],
    captureContext: string,
    send: boolean,
    drafts: (EmailDraft | null)[],
    tags: string[]
  ) => Promise<CardSaveOutcome[]>;
  /** Start the GoHighLevel OAuth flow — offered when the save fails because
   *  this customer has no usable GHL connection. */
  onConnectGhl?: () => void;
}

/** One line of the post-scan record written into the chat thread. */
export interface ScanReportRow {
  name: string;
  company: string | null;
  tags: string[];
  sent: boolean | null;
  error: string | null;
}

/** Per-card save state, keyed by the contact's client id. */
interface SaveState {
  status: "idle" | "saving" | "saved" | "error";
  outcome?: CardSaveOutcome;
  error?: string;
}

/** One captured (or uploaded) photo, before extraction. */
interface Shot {
  id: string;
  file: File;
  /** Object URL for the thumbnail — revoked when the shot is dropped. */
  previewUrl: string;
}

type CameraState = "starting" | "live" | "unavailable";
type FacingMode = "environment" | "user";

// Guide geometry and auto-capture tuning, lifted from the marketing site's
// CameraCapture (daisy-marketing-website/app/card-to-crm/CameraCapture.tsx).
// These thresholds are already tuned against real cards in production — don't
// re-derive them, and change them in both places if they ever move.
const CARD_AR = 1.75; // business card, landscape
const GUIDE_W = 0.86; // guide width as a fraction of the preview
const FOCUS_MIN = 9; // mean gradient energy inside the guide (blurry < this)
const MOTION_MAX = 5; // mean abs frame-to-frame diff (steady < this)
const READY_TARGET = 6; // consecutive good ticks (~0.8s) before auto-snap
const TICK_MS = 130;
/** After an auto-snap, how long before the loop re-arms for the next card.
 *  The marketing site never re-arms — it captures one card and stops. This
 *  panel batches up to MAX_IMAGES_PER_EXTRACT, so it has to. */
const REARM_MS = 1400;

const newId = (): string =>
  typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`;

/**
 * Map the on-screen card guide to a source rectangle in the video's own pixels,
 * accounting for object-cover scaling. Ported from the marketing site so the
 * upload carries just the card instead of the whole room — the model reads a
 * cropped card far more reliably, and the payload is a fraction of the size.
 */
function srcRect(video: HTMLVideoElement, container: HTMLElement) {
  const cw = container.clientWidth;
  const ch = container.clientHeight;
  const vw = video.videoWidth;
  const vh = video.videoHeight;
  if (!cw || !ch || !vw || !vh) return null;

  const scale = Math.max(cw / vw, ch / vh); // object-cover
  const offX = (cw - vw * scale) / 2;
  const offY = (ch - vh * scale) / 2;

  const gw = GUIDE_W * cw;
  const gh = gw / CARD_AR;
  const gx = (cw - gw) / 2;
  const gy = (ch - gh) / 2;

  let sx = (gx - offX) / scale;
  let sy = (gy - offY) / scale;
  let sw = gw / scale;
  let sh = gh / scale;
  sx = Math.max(0, Math.min(sx, vw));
  sy = Math.max(0, Math.min(sy, vh));
  sw = Math.min(sw, vw - sx);
  sh = Math.min(sh, vh - sy);
  return { sx, sy, sw, sh };
}

/**
 * In-chat business-card capture (SGL-95). Live camera with a card guide, focus
 * and steadiness analysis, and auto-capture; shoots up to
 * MAX_IMAGES_PER_EXTRACT cards, extracts them in one request, and shows the
 * result as editable contacts.
 *
 * Falls back to a file picker whenever the camera can't run — permission
 * denied, no camera, or a non-secure origin (getUserMedia is HTTPS-only, so
 * plain-http dev hosts land here too).
 */
export const CardCapturePanel: React.FC<CardCapturePanelProps> = ({
  isOpen,
  onClose,
  onScanComplete,
  onExtract,
  onHandOffToChat,
  onDraftIntros,
  onSaveContact,
  onSaveAll,
  onConnectGhl,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const analysisRef = useRef<HTMLCanvasElement | null>(null);
  const prevGrayRef = useRef<Uint8ClampedArray | null>(null);
  const readyRef = useRef(0);
  const capturedRef = useRef(false);
  const tickRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const rearmRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Live mirrors for the analysis loop, which is created once per stream and
   *  would otherwise close over stale state. */
  const autoEnabledRef = useRef(true);
  const shotCountRef = useRef(0);
  /** Generation counter: discards a getUserMedia promise that resolves after
   *  the panel closed or the camera was flipped, so no stream is leaked. */
  const genRef = useRef(0);

  const [facingMode, setFacingMode] = useState<FacingMode>("environment");
  const [cameraState, setCameraState] = useState<CameraState>("starting");
  const [shots, setShots] = useState<Shot[]>([]);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [extracting, setExtracting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState(0); // 0..1 readiness
  const [status, setStatus] = useState("Fit the card inside the frame");
  const [autoEnabled, setAutoEnabled] = useState(true);
  const [flash, setFlash] = useState(false);
  /** Which half of the panel is showing. Derived from contacts.length before
   *  "Scan more" existed — but that made returning to the camera with cards
   *  already in hand impossible. */
  const [mode, setMode] = useState<"capture" | "review">("capture");
  const [captureContext, setCaptureContext] = useState("");
  // Speak the note instead of typing it. An owner standing outside after a
  // networking event will not type "met Bernard at the Georgetown luncheon,
  // would love a one to one" on a phone, and that sentence is what makes the
  // drafted email worth sending.
  const dictation = useVoiceDictation({
    value: captureContext,
    onChange: setCaptureContext,
  });
  const [saveStates, setSaveStates] = useState<Record<string, SaveState>>({});
  const [savingAll, setSavingAll] = useState(false);
  /** Which cards should also get the intro email sent. Defaults to on per card,
   *  and only ever applies to a card that has an email address. */
  const [sendFor, setSendFor] = useState<Record<string, boolean>>({});
  /** The editable intro per card. Composed on entering review so the user reads
   *  and can change it BEFORE anything is saved or sent — the whole point of
   *  drafting server-side ahead of the save. */
  const [drafts, setDrafts] = useState<Record<string, EmailDraft>>({});
  const [draftingIds, setDraftingIds] = useState<string[]>([]);
  /** Cards whose intro couldn't be written. Silently leaving the fields blank
   *  read as "the model produced nothing", which is indistinguishable from a
   *  failed request — say which it was, and offer a retry. */
  const [draftFailedIds, setDraftFailedIds] = useState<string[]>([]);
  /** Tags applied to every card in this scan. Batch-level rather than per-card:
   *  a scan is one event, and per-card overrides would double the UI for a case
   *  that barely comes up — a stray card can be deleted and rescanned. */
  const [tags, setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState("");
  /** Bedrock's suggestion for this scan, offered as a chip the user adds — never
   *  applied on its own. Same contract as SGL-81's suggested_tag. */
  const [suggestedTag, setSuggestedTag] = useState<string | null>(null);
  /** The Escape listener is registered once; without this it would close over
   *  the first render's (empty) contacts and report nothing. */
  const closeWithReportRef = useRef<() => void>(() => {});
  /** Set when GHL itself is the problem (not one bad contact) — offers reconnect. */
  const [connectionProblem, setConnectionProblem] = useState<string | null>(null);

  const atShotLimit = shots.length >= MAX_IMAGES_PER_EXTRACT;

  useEffect(() => {
    autoEnabledRef.current = autoEnabled;
  }, [autoEnabled]);
  useEffect(() => {
    shotCountRef.current = shots.length;
  }, [shots.length]);

  const stopStream = useCallback(() => {
    if (tickRef.current) clearInterval(tickRef.current);
    tickRef.current = null;
    if (rearmRef.current) clearTimeout(rearmRef.current);
    rearmRef.current = null;
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  }, []);

  const addShot = useCallback((file: File) => {
    setShots((prev) =>
      prev.length >= MAX_IMAGES_PER_EXTRACT
        ? prev
        : [...prev, { id: newId(), file, previewUrl: URL.createObjectURL(file) }]
    );
  }, []);

  // --- Capture (crops to the guide region) ----------------------------------
  const doCapture = useCallback(() => {
    const video = videoRef.current;
    const container = containerRef.current;
    if (!video || !container || !video.videoWidth) return;
    if (capturedRef.current || shotCountRef.current >= MAX_IMAGES_PER_EXTRACT)
      return;
    const guide = srcRect(video, container);
    if (!guide) return;
    const rect = captureRect(guide, video.videoWidth, video.videoHeight);

    capturedRef.current = true;
    setFlash(true);

    const canvas = document.createElement("canvas");
    canvas.width = Math.round(rect.sw);
    canvas.height = Math.round(rect.sh);
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.drawImage(
      video,
      rect.sx,
      rect.sy,
      rect.sw,
      rect.sh,
      0,
      0,
      canvas.width,
      canvas.height
    );
    canvas.toBlob(
      (blob) => {
        if (!blob) return;
        addShot(
          new File([blob], `card-${Date.now()}.jpg`, { type: "image/jpeg" })
        );
      },
      "image/jpeg",
      0.92
    );

    // Re-arm for the next card rather than freezing on a review step — this
    // panel is a batch flow, and a bad shot is dropped from the thumbnail strip.
    rearmRef.current = setTimeout(() => {
      capturedRef.current = false;
      readyRef.current = 0;
      prevGrayRef.current = null;
      setProgress(0);
      setFlash(false);
      setStatus("Fit the card inside the frame");
    }, REARM_MS);
  }, [addShot]);

  // --- Live analysis: focus + steadiness inside the guide --------------------
  const analyze = useCallback(() => {
    const video = videoRef.current;
    const container = containerRef.current;
    if (!video || !container || !video.videoWidth || capturedRef.current) return;
    if (shotCountRef.current >= MAX_IMAGES_PER_EXTRACT) return;
    const rect = srcRect(video, container);
    if (!rect) return;

    if (!analysisRef.current)
      analysisRef.current = document.createElement("canvas");
    const aw = 200;
    const ah = Math.round(aw / CARD_AR);
    const canvas = analysisRef.current;
    canvas.width = aw;
    canvas.height = ah;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    if (!ctx) return;

    ctx.drawImage(video, rect.sx, rect.sy, rect.sw, rect.sh, 0, 0, aw, ah);
    const { data } = ctx.getImageData(0, 0, aw, ah);

    const gray = new Uint8ClampedArray(aw * ah);
    for (let i = 0, p = 0; i < data.length; i += 4, p++) {
      gray[p] = (data[i] * 299 + data[i + 1] * 587 + data[i + 2] * 114) / 1000;
    }

    // Focus: mean gradient magnitude.
    let gradSum = 0;
    let gradN = 0;
    for (let row = 0; row < ah; row++) {
      for (let col = 1; col < aw; col++) {
        const idx = row * aw + col;
        gradSum += Math.abs(gray[idx] - gray[idx - 1]);
        if (row > 0) gradSum += Math.abs(gray[idx] - gray[idx - aw]);
        gradN += 2;
      }
    }
    const focus = gradN ? gradSum / gradN : 0;

    // Motion: mean abs diff vs the previous frame.
    let motion = Infinity;
    const prev = prevGrayRef.current;
    if (prev && prev.length === gray.length) {
      let diff = 0;
      for (let i = 0; i < gray.length; i++) diff += Math.abs(gray[i] - prev[i]);
      motion = diff / gray.length;
    }
    prevGrayRef.current = gray;

    const good = focus >= FOCUS_MIN && motion <= MOTION_MAX;
    readyRef.current = good
      ? Math.min(READY_TARGET, readyRef.current + 1)
      : Math.max(0, readyRef.current - 1.5);
    setProgress(readyRef.current / READY_TARGET);

    if (focus < FOCUS_MIN) setStatus("Fit the card inside the frame");
    else if (motion > MOTION_MAX) setStatus("Hold steady…");
    else setStatus("Capturing…");

    if (autoEnabledRef.current && readyRef.current >= READY_TARGET) doCapture();
  }, [doCapture]);

  // --- Stream lifecycle -----------------------------------------------------
  const startStream = useCallback(
    async (mode: FacingMode) => {
      setError(null);
      setCameraState("starting");
      readyRef.current = 0;
      prevGrayRef.current = null;
      capturedRef.current = false;
      setProgress(0);
      stopStream();
      const gen = ++genRef.current;

      if (!navigator.mediaDevices?.getUserMedia) {
        setCameraState("unavailable");
        return;
      }
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          // `ideal`, not `exact`: a laptop has no environment-facing camera and
          // `exact` would reject outright instead of handing back the webcam.
          video: { facingMode: { ideal: mode }, width: { ideal: 1920 } },
          audio: false,
        });
        if (gen !== genRef.current) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        const video = videoRef.current;
        if (video) {
          video.srcObject = stream;
          await video.play().catch(() => {
            /* autoplay rejection is non-fatal — the frame still renders */
          });
        }
        setCameraState("live");
        tickRef.current = setInterval(analyze, TICK_MS);
      } catch {
        if (gen === genRef.current) setCameraState("unavailable");
      }
    },
    [analyze, stopStream]
  );

  // Start on open and on flip; stop on close/unmount. Leaving tracks running
  // keeps the device's camera indicator lit after the panel is gone.
  useEffect(() => {
    if (!isOpen) return;
    const generation = genRef;
    startStream(facingMode);
    return () => {
      // Invalidate any in-flight getUserMedia so a late-resolving promise can't
      // attach a stream after this effect is gone.
      generation.current++;
      stopStream();
    };
    // startStream is recreated whenever `analyze` is; re-running on that would
    // restart the camera mid-session for no reason.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, facingMode]);

  // Full-screen sheet on mobile: stop the chat thread behind it from scrolling.
  useEffect(() => {
    if (!isOpen) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [isOpen]);

  // Escape closes; Tab stays inside the dialog.
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        closeWithReportRef.current();
        return;
      }
      if (e.key === "Tab" && dialogRef.current) {
        const focusables = Array.from(
          dialogRef.current.querySelectorAll<HTMLElement>(
            'button, [href], input, textarea, [tabindex]:not([tabindex="-1"])'
          )
        ).filter((el) => !el.hasAttribute("disabled"));
        if (!focusables.length) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  // Move focus into the dialog on open; restore it on close.
  useEffect(() => {
    if (!isOpen) return;
    const prevFocus = document.activeElement as HTMLElement | null;
    dialogRef.current?.focus();
    return () => prevFocus?.focus?.();
  }, [isOpen]);

  // Once every card is in the CRM the panel has nothing left to do, so it closes
  // itself and the summary lands in the thread. The short delay lets the
  // "Saved to CRM" / "Intro sent" badges register before it disappears —
  // vanishing instantly reads as "did that work?".
  // Goes through the ref because closeWithReport is declared after this effect.
  useEffect(() => {
    if (!isOpen || mode !== "review" || !contacts.length) return;
    const allSaved = contacts.every(
      (c) => saveStates[c.id]?.status === "saved"
    );
    if (!allSaved) return;
    const timer = setTimeout(() => closeWithReportRef.current(), 1200);
    return () => clearTimeout(timer);
  }, [isOpen, mode, contacts, saveStates]);

  // Thumbnails hold object URLs; release them when the panel closes.
  useEffect(() => {
    if (isOpen) return;
    setShots((prev) => {
      prev.forEach((s) => URL.revokeObjectURL(s.previewUrl));
      return [];
    });
    setContacts([]);
    setError(null);
    setExtracting(false);
    setFlash(false);
    setProgress(0);
    setCaptureContext("");
    setSaveStates({});
    setConnectionProblem(null);
    setMode("capture");
    setDrafts({});
    setSendFor({});
    setDraftingIds([]);
    setDraftFailedIds([]);
    setTags([]);
    setTagInput("");
    setSuggestedTag(null);
  }, [isOpen]);

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    Array.from(e.target.files ?? [])
      .filter((f) => f.type.startsWith("image/"))
      .slice(0, MAX_IMAGES_PER_EXTRACT - shots.length)
      .forEach(addShot);
    e.target.value = ""; // let the same file be picked again
  };

  const removeShot = (id: string) =>
    setShots((prev) => {
      const target = prev.find((s) => s.id === id);
      if (target) URL.revokeObjectURL(target.previewUrl);
      return prev.filter((s) => s.id !== id);
    });

  // --- Extraction -----------------------------------------------------------
  const handleExtract = async () => {
    if (!shots.length || extracting) return;
    setExtracting(true);
    setError(null);
    try {
      const images = await filesToCompressedDataUrls(shots.map((s) => s.file));
      const extracted = await onExtract(images);
      if (!extracted.length) {
        setError("No contact details found — try a straighter, closer photo.");
        return;
      }
      stopStream(); // nothing left to film
      shots.forEach((shot) => URL.revokeObjectURL(shot.previewUrl));
      setShots([]);
      // SGL-346: the drafts are reviewed in the chat now, not in here. The panel's
      // job ends at capture — hand the extracted cards over and close, so the owner
      // reads and approves the emails in the same place they talk to the agent.
      if (onHandOffToChat) {
        await onHandOffToChat([...contacts, ...extracted]);
        onClose();
        return;
      }
      // Fallback: the in-panel review, still here as the rollback path until the
      // chat flow is proven (plan phase 6 retires it).
      setContacts((prev) => [...prev, ...extracted]);
      void composeDrafts(extracted);
      setMode("review");
    } catch (err: any) {
      setError(err?.message || "Extraction failed. Try again in a moment.");
    } finally {
      setExtracting(false);
    }
  };

  /** Compose intros for freshly extracted cards. Failure is non-fatal: the card
   *  is still reviewable and savable, just without a prewritten email. */
  const composeDrafts = async (forContacts: Contact[]) => {
    const withEmail = forContacts.filter((c) => c.email);
    if (!withEmail.length) return;
    const ids = new Set(withEmail.map((c) => c.id));
    setDraftingIds((prev) => [...prev, ...withEmail.map((c) => c.id)]);
    setDraftFailedIds((prev) => prev.filter((id) => !ids.has(id)));
    try {
      const { drafts: composed, suggestedTag: suggestion } = await onDraftIntros(
        withEmail,
        captureContext.trim()
      );
      if (suggestion) setSuggestedTag(suggestion);
      const failed: string[] = [];
      setDrafts((prev) => {
        const next = { ...prev };
        withEmail.forEach((c, i) => {
          const draft = composed[i];
          if (draft) next[c.id] = draft;
          else failed.push(c.id);
        });
        return next;
      });
      if (failed.length) setDraftFailedIds((prev) => [...prev, ...failed]);
    } catch {
      // Saving still works without an intro — this only costs the email.
      setDraftFailedIds((prev) => [...prev, ...withEmail.map((c) => c.id)]);
    } finally {
      setDraftingIds((prev) => prev.filter((id) => !ids.has(id)));
    }
  };

  const addTag = (raw: string) => {
    const tag = raw.trim().replace(/,+$/, "").slice(0, 50);
    if (!tag) return;
    setTags((prev) =>
      prev.some((t) => t.toLowerCase() === tag.toLowerCase()) || prev.length >= 10
        ? prev
        : [...prev, tag]
    );
    setTagInput("");
  };

  const handleDraftChange = (id: string, field: keyof EmailDraft, value: string) =>
    setDrafts((prev) => ({
      ...prev,
      [id]: {
        subject: prev[id]?.subject ?? "",
        body: prev[id]?.body ?? "",
        [field]: value,
      },
    }));

  const handleFieldChange = (id: string, field: ContactField, value: string) =>
    setContacts((prev) =>
      prev.map((c) => (c.id === id ? { ...c, [field]: value } : c))
    );

  /** Back to the camera, keeping every card already scanned. */
  const handleScanMore = () => {
    shots.forEach((shot) => URL.revokeObjectURL(shot.previewUrl));
    setShots([]);
    setError(null);
    setConnectionProblem(null);
    setMode("capture");
    startStream(facingMode);
  };

  /** Throw everything away and start over. */
  const handleStartOver = () => {
    shots.forEach((shot) => URL.revokeObjectURL(shot.previewUrl));
    setShots([]);
    setContacts([]);
    setError(null);
    setSaveStates({});
    setConnectionProblem(null);
    setMode("capture");
    startStream(facingMode);
  };

  // --- Save to GHL ----------------------------------------------------------
  /** A GHL-level failure (no connection / needs reauth) is not the contact's
   *  fault and is fixable by the user, so it is surfaced separately with a
   *  reconnect action instead of being written onto each card. */
  const handleSaveFailure = (err: any) => {
    const reason = err?.reason;
    if (reason === "not_connected" || reason === "needs_reauth") {
      setConnectionProblem(err.message);
      return true;
    }
    return false;
  };

  /** Default on, but never for a card with no email to send to. */
  const shouldSend = (contact: Contact) =>
    Boolean(contact.email) &&
    sendFor[contact.id] !== false &&
    // Clearing the draft is how you opt out of sending without hunting for the
    // checkbox — there is nothing approved to send.
    Boolean(drafts[contact.id]?.subject?.trim() && drafts[contact.id]?.body?.trim());

  const handleSaveOne = async (contact: Contact) => {
    setConnectionProblem(null);
    setSaveStates((prev) => ({ ...prev, [contact.id]: { status: "saving" } }));
    try {
      const outcome = await onSaveContact(
        contact,
        captureContext.trim(),
        shouldSend(contact),
        drafts[contact.id] ?? null,
        tags
      );
      setSaveStates((prev) => ({
        ...prev,
        [contact.id]: { status: "saved", outcome },
      }));
    } catch (err: any) {
      const connectionIssue = handleSaveFailure(err);
      setSaveStates((prev) => ({
        ...prev,
        [contact.id]: {
          status: "error",
          error: connectionIssue ? undefined : err?.message || "Save failed.",
        },
      }));
    }
  };

  const handleSaveAll = async () => {
    const pending = contacts.filter((c) => saveStates[c.id]?.status !== "saved");
    if (!pending.length || savingAll) return;
    setSavingAll(true);
    setConnectionProblem(null);
    setSaveStates((prev) => {
      const next = { ...prev };
      pending.forEach((c) => {
        next[c.id] = { status: "saving" };
      });
      return next;
    });
    try {
      // One flag for the batch: send only if every pending card is opted in,
      // so Save-all can't quietly mail a card the user switched off.
      const outcomes = await onSaveAll(
        pending,
        captureContext.trim(),
        pending.every(shouldSend),
        pending.map((c) => drafts[c.id] ?? null),
        tags
      );
      setSaveStates((prev) => {
        const next = { ...prev };
        pending.forEach((c, i) => {
          const outcome = outcomes[i];
          next[c.id] = outcome?.ok
            ? { status: "saved", outcome }
            : { status: "error", error: outcome?.error || "Save failed." };
        });
        return next;
      });
    } catch (err: any) {
      const connectionIssue = handleSaveFailure(err);
      setSaveStates((prev) => {
        const next = { ...prev };
        pending.forEach((c) => {
          next[c.id] = {
            status: "error",
            error: connectionIssue ? undefined : err?.message || "Save failed.",
          };
        });
        return next;
      });
    } finally {
      setSavingAll(false);
    }
  };

  /** Hand the thread a record of what actually reached the CRM. Runs on close
   *  rather than per save, so one scan session produces one report however many
   *  times the user pressed Save. */
  const closeWithReport = () => {
    const rows: ScanReportRow[] = contacts
      .filter((c) => saveStates[c.id]?.status === "saved")
      .map((c) => {
        const outcome = saveStates[c.id]?.outcome;
        return {
          name: c.name || c.company || c.email || "Unnamed card",
          company: c.company,
          tags: outcome?.ghl?.tags ?? [],
          sent: outcome?.sent ?? null,
          error: outcome?.sendError ?? null,
        };
      });
    if (rows.length) onScanComplete(rows);
    onClose();
  };
  closeWithReportRef.current = closeWithReport;

  if (!isOpen) return null;

  const reviewing = mode === "review" && contacts.length > 0;
  const atCardLimit = contacts.length >= MAX_IMAGES_PER_EXTRACT;
  const savingOne = Object.values(saveStates).some((v) => v.status === "saving");
  const busy = extracting || savingAll || savingOne;
  const pendingContacts = contacts.filter(
    (c) => saveStates[c.id]?.status !== "saved"
  );
  const sendAllWillSend =
    pendingContacts.length > 0 && pendingContacts.every(shouldSend);
  const savedContacts = contacts.filter(
    (c) => saveStates[c.id]?.status === "saved"
  );
  const unsavedCount = contacts.length - savedContacts.length;
  const guideColor =
    progress >= 1
      ? "var(--color-primary)"
      : progress > 0.3
      ? "#e4b372"
      : "rgba(255,255,255,0.85)";

  return (
    <div
      className="fixed inset-0 z-50 flex items-stretch justify-center bg-black/60 sm:items-center sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Scan business card"
      onClick={busy ? undefined : closeWithReport}
    >
      <div
        ref={dialogRef}
        tabIndex={-1}
        // 100dvh, not 100vh: mobile Safari's collapsing URL bar otherwise pushes
        // the shutter row off the bottom of the screen.
        className="flex h-[100dvh] w-full flex-col bg-theme-surface outline-none sm:h-[min(90dvh,44rem)] sm:max-w-lg sm:rounded-2xl sm:border sm:border-theme-border sm:shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-3 border-b border-theme-border px-4 py-3 pt-[max(0.75rem,env(safe-area-inset-top))] sm:pt-3">
          <div>
            <h3 className="text-base font-bold text-theme-fg">
              {reviewing ? "Review contacts" : "Scan business card"}
            </h3>
            <p className="mt-0.5 text-[12px] text-theme-fg-muted">
              {reviewing
                ? "Fix anything the scan misread before you use it."
                : contacts.length
                ? `${contacts.length} card${contacts.length > 1 ? "s" : ""} scanned so far — line up the next one.`
                : `Hold the card in the frame — it snaps by itself. Up to ${MAX_IMAGES_PER_EXTRACT} per scan.`}
            </p>
          </div>
          <button
            type="button"
            onClick={closeWithReport}
            disabled={busy}
            className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg text-theme-fg-muted hover:bg-theme-background hover:text-theme-fg disabled:opacity-50"
            aria-label="Close"
          >
            <span className="material-icons-outlined text-[20px]">close</span>
          </button>
        </div>

        {/* Body */}
        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
          {reviewing ? (
            <div className="flex-none space-y-3 p-4">
              {/* Capture context feeds the drafted intro email — "met at the
                  June mixer" is what makes the draft worth sending. */}
              <label className="block">
                <span className="mb-1 block text-[11px] font-medium text-theme-fg-muted">
                  Where did you meet? (optional — used in the drafted email)
                </span>
                <div className="relative">
                  <input
                    type="text"
                    value={captureContext}
                    disabled={busy || dictation.isTranscribing}
                    placeholder={
                      dictation.isRecording
                        ? "Listening…"
                        : dictation.isTranscribing
                        ? "Writing that down…"
                        : "Tap the microphone and say where you met"
                    }
                    onChange={(e) => setCaptureContext(e.target.value)}
                    className="w-full rounded-lg border border-theme-border bg-theme-surface py-2 pl-2.5 pr-10 text-[13px] text-theme-fg outline-none focus:border-theme-primary disabled:opacity-60"
                  />
                  <button
                    type="button"
                    onClick={dictation.toggle}
                    disabled={busy || dictation.isTranscribing}
                    aria-label={dictation.isRecording ? "Stop dictating" : "Dictate where you met"}
                    aria-pressed={dictation.isRecording}
                    className={`absolute right-1 top-1/2 -translate-y-1/2 rounded-full p-1.5 transition-colors disabled:opacity-50 ${
                      dictation.isRecording
                        ? "bg-red-500/15 text-red-600"
                        : "text-theme-fg-muted hover:bg-theme-border/60 hover:text-theme-fg"
                    }`}
                  >
                    <span className="material-icons-outlined text-[18px]">
                      {dictation.isTranscribing ? "hourglass_empty" : dictation.isRecording ? "stop_circle" : "mic"}
                    </span>
                  </button>
                </div>
                {dictation.error && (
                  <span className="mt-1 block text-[11px] text-red-600">{dictation.error}</span>
                )}
              </label>

              {/* One tag set for the whole scan — a scan is one event. Applied
                  to every card on save, additively, so a returning contact
                  keeps whatever tags they already had. */}
              <div>
                <span className="mb-1 block text-[11px] font-medium text-theme-fg-muted">
                  CRM tags (optional — applied to every card in this scan)
                </span>
                <div className="flex flex-wrap items-center gap-1.5 rounded-lg border border-theme-border bg-theme-surface px-2 py-1.5">
                  {tags.map((tag) => (
                    <span
                      key={tag}
                      className="inline-flex items-center gap-1 rounded-full bg-theme-primary/15 px-2 py-0.5 text-[12px] font-medium text-theme-primary"
                    >
                      {tag}
                      <button
                        type="button"
                        onClick={() =>
                          setTags((prev) => prev.filter((t) => t !== tag))
                        }
                        disabled={busy}
                        aria-label={`Remove tag ${tag}`}
                        className="hover:opacity-70 disabled:opacity-50"
                      >
                        <span className="material-icons-outlined text-[13px]">
                          close
                        </span>
                      </button>
                    </span>
                  ))}
                  <input
                    type="text"
                    value={tagInput}
                    disabled={busy || tags.length >= 10}
                    placeholder={tags.length ? "Add another…" : "e.g. BNI June"}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === ",") {
                        e.preventDefault();
                        addTag(tagInput);
                      } else if (
                        e.key === "Backspace" &&
                        !tagInput &&
                        tags.length
                      ) {
                        setTags((prev) => prev.slice(0, -1));
                      }
                    }}
                    // Typing a tag and clicking Save straight away would
                    // otherwise drop it — commit on blur too.
                    onBlur={() => addTag(tagInput)}
                    className="min-w-[8rem] flex-1 bg-transparent px-1 py-0.5 text-[13px] text-theme-fg outline-none disabled:opacity-60"
                  />
                </div>
                {suggestedTag &&
                  !tags.some(
                    (t) => t.toLowerCase() === suggestedTag.toLowerCase()
                  ) && (
                    <button
                      type="button"
                      onClick={() => addTag(suggestedTag)}
                      disabled={busy}
                      className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-bold text-theme-primary hover:opacity-80 disabled:opacity-50"
                    >
                      <span className="material-icons-outlined text-[13px]">add</span>
                      Suggested: {suggestedTag}
                    </button>
                  )}
              </div>

              {contacts.map((contact, index) => {
                const save = saveStates[contact.id];
                const saved = save?.status === "saved";
                return (
                <div
                  key={contact.id}
                  className="rounded-xl border border-theme-border bg-theme-background p-3"
                >
                  <div className="mb-2 flex items-center justify-between">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-[10px] font-semibold uppercase tracking-widest text-theme-fg-muted">
                        Contact {index + 1}
                      </span>
                      {saved && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-theme-primary/15 px-2 py-0.5 text-[11px] font-bold text-theme-primary">
                          <span className="material-icons-outlined text-[13px]">
                            check_circle
                          </span>
                          {save?.outcome?.ghl?.is_new === false
                            ? "Updated in CRM"
                            : "Saved to CRM"}
                        </span>
                      )}
                      {saved && (save?.outcome?.ghl?.tags ?? []).length > 0 && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-theme-primary/10 px-2 py-0.5 text-[11px] font-medium text-theme-primary">
                          <span className="material-icons-outlined text-[13px]">
                            sell
                          </span>
                          {(save?.outcome?.ghl?.tags ?? []).join(", ")}
                        </span>
                      )}
                      {saved && save?.outcome?.ghl?.tagged === false && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-theme-danger/15 px-2 py-0.5 text-[11px] font-bold text-theme-danger">
                          <span className="material-icons-outlined text-[13px]">
                            sell
                          </span>
                          Tagging failed
                        </span>
                      )}
                      {saved && save?.outcome?.sent === true && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-theme-primary/15 px-2 py-0.5 text-[11px] font-bold text-theme-primary">
                          <span className="material-icons-outlined text-[13px]">send</span>
                          Intro sent
                        </span>
                      )}
                      {saved && save?.outcome?.sent === false && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-theme-danger/15 px-2 py-0.5 text-[11px] font-bold text-theme-danger">
                          <span className="material-icons-outlined text-[13px]">
                            error_outline
                          </span>
                          Email not sent
                        </span>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() =>
                        setContacts((prev) =>
                          prev.filter((c) => c.id !== contact.id)
                        )
                      }
                      disabled={busy}
                      className="text-theme-fg-muted hover:text-theme-danger disabled:opacity-50"
                      aria-label={`Remove contact ${index + 1}`}
                    >
                      <span className="material-icons-outlined text-[18px]">
                        delete_outline
                      </span>
                    </button>
                  </div>
                  <ContactFieldsEditor
                    contact={contact}
                    disabled={busy || saved}
                    onChange={(field, value) =>
                      handleFieldChange(contact.id, field, value)
                    }
                  />

                  {save?.status === "error" && save.error && (
                    <p className="mt-2 text-[12px] font-medium text-theme-danger">
                      {save.error}
                    </p>
                  )}

                  {/* Editable BEFORE saving: the email goes out on the same
                      click as the save, so this is the only chance to read and
                      change it. After saving it becomes a read-only record. */}
                  {!saved && contact.email && (
                    <div className="mt-3 rounded-lg border border-theme-border bg-theme-surface p-2.5">
                      <div className="mb-1.5 flex items-center justify-between gap-2">
                        <p className="text-[10px] font-semibold uppercase tracking-widest text-theme-fg-muted">
                          Intro email — edit before sending
                        </p>
                        {draftingIds.includes(contact.id) ? (
                          <span className="flex items-center gap-1 text-[11px] text-theme-fg-muted">
                            <span className="h-3 w-3 animate-spin rounded-full border-2 border-theme-border border-t-theme-primary" />
                            Writing…
                          </span>
                        ) : (
                          <button
                            type="button"
                            onClick={() => void composeDrafts([contact])}
                            disabled={busy}
                            className="inline-flex items-center gap-1 text-[11px] font-bold text-theme-primary hover:opacity-80 disabled:opacity-50"
                          >
                            <span className="material-icons-outlined text-[13px]">
                              auto_awesome
                            </span>
                            {drafts[contact.id] ? "Rewrite" : "Write intro"}
                          </button>
                        )}
                      </div>
                      <input
                        type="text"
                        value={drafts[contact.id]?.subject ?? ""}
                        placeholder="Subject"
                        disabled={busy || save?.status === "saving"}
                        onChange={(e) =>
                          handleDraftChange(contact.id, "subject", e.target.value)
                        }
                        className="mb-1.5 w-full rounded-lg border border-theme-border bg-theme-background px-2.5 py-2 text-[13px] font-bold text-theme-fg outline-none focus:border-theme-primary disabled:opacity-60"
                      />
                      {draftFailedIds.includes(contact.id) && (
                        <p className="mb-1.5 text-[11px] font-medium text-theme-danger">
                          Couldn&rsquo;t write the intro. Retry, or type one
                          yourself — the contact still saves either way.
                        </p>
                      )}
                      <textarea
                        value={drafts[contact.id]?.body ?? ""}
                        placeholder={
                          draftingIds.includes(contact.id)
                            ? "Writing the intro…"
                            : "Write the intro email, or leave blank to skip sending."
                        }
                        rows={6}
                        disabled={busy || save?.status === "saving"}
                        onChange={(e) =>
                          handleDraftChange(contact.id, "body", e.target.value)
                        }
                        className="w-full resize-y rounded-lg border border-theme-border bg-theme-background px-2.5 py-2 text-[12.5px] text-theme-fg outline-none focus:border-theme-primary disabled:opacity-60"
                      />
                    </div>
                  )}

                  {/* After saving: what actually went out (or didn't). */}
                  {saved && save?.outcome?.draft && (
                    <div className="mt-3 rounded-lg border border-theme-border bg-theme-surface p-2.5">
                      <p className="mb-1 text-[10px] font-semibold uppercase tracking-widest text-theme-fg-muted">
                        {save?.outcome?.sent === true
                          ? "Intro email — sent"
                          : "Draft — not sent"}
                      </p>
                      <p className="text-[12.5px] font-bold text-theme-fg">
                        {save.outcome.draft.subject}
                      </p>
                      <p className="mt-1 whitespace-pre-wrap text-[12px] text-theme-fg-muted">
                        {save.outcome.draft.body}
                      </p>
                      <p className="mt-2 text-[11px] text-theme-fg-muted">
                        {save?.outcome?.sent === true
                          ? `Delivered to ${contact.email} from your GoHighLevel account.`
                          : save?.outcome?.sent === false
                          ? "Sending failed — the contact is saved. Send it from GoHighLevel."
                          : "Review and send it from your GoHighLevel account."}
                      </p>
                    </div>
                  )}
                  {saved && !save?.outcome?.draft && (
                    <p className="mt-2 text-[11px] text-theme-fg-muted">
                      Saved to GoHighLevel. No intro email was sent.
                    </p>
                  )}

                  {!saved && (
                    <div className="mt-3 flex flex-wrap items-center gap-3">
                      <button
                        type="button"
                        onClick={() => handleSaveOne(contact)}
                        disabled={busy || save?.status === "saving"}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-theme-primary px-3 py-1.5 text-[12px] font-bold text-theme-primary hover:bg-theme-primary/10 disabled:opacity-50"
                      >
                        {save?.status === "saving" && (
                          <span className="h-3 w-3 animate-spin rounded-full border-2 border-theme-primary/40 border-t-theme-primary" />
                        )}
                        {save?.status === "saving"
                          ? "Saving…"
                          : shouldSend(contact)
                          ? "Save & send intro"
                          : "Save to CRM"}
                      </button>

                      {/* The email goes out on the same click, so the choice has
                          to be visible before it — not buried after the fact. */}
                      {contact.email ? (
                        <label className="inline-flex cursor-pointer items-center gap-1.5 text-[12px] text-theme-fg-muted">
                          <input
                            type="checkbox"
                            checked={sendFor[contact.id] !== false}
                            disabled={busy || save?.status === "saving"}
                            onChange={(e) =>
                              setSendFor((prev) => ({
                                ...prev,
                                [contact.id]: e.target.checked,
                              }))
                            }
                            className="h-3.5 w-3.5 accent-current"
                          />
                          Send intro email to {contact.email}
                        </label>
                      ) : (
                        <span className="text-[12px] text-theme-fg-muted">
                          No email on this card — nothing to send.
                        </span>
                      )}
                    </div>
                  )}
                </div>
                );
              })}

              {connectionProblem && (
                <div className="rounded-xl border border-theme-danger/40 bg-theme-danger/5 p-3">
                  <p className="text-[12px] font-medium text-theme-danger">
                    {connectionProblem}
                  </p>
                  {onConnectGhl && (
                    <button
                      type="button"
                      onClick={onConnectGhl}
                      className="mt-2 inline-flex items-center gap-1.5 rounded-lg bg-theme-primary px-3 py-1.5 text-[12px] font-bold text-white hover:opacity-90"
                    >
                      Connect GoHighLevel
                    </button>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="flex min-h-0 flex-1 flex-col p-4">
              {cameraState === "unavailable" ? (
                <div className="flex min-h-[9rem] flex-1 flex-col items-center justify-center rounded-2xl border-2 border-dashed border-theme-border bg-theme-background px-6 py-8 text-center">
                  <span className="material-icons-outlined mb-2 text-[32px] text-theme-fg-muted">
                    no_photography
                  </span>
                  <p className="text-[13px] font-medium text-theme-fg">
                    Camera unavailable
                  </p>
                  <p className="mt-1 text-[12px] text-theme-fg-muted">
                    No camera, permission denied, or the page isn&rsquo;t on
                    HTTPS. Retry, or upload a photo instead.
                  </p>
                  <button
                    type="button"
                    onClick={() => startStream(facingMode)}
                    className="mt-3 inline-flex items-center gap-1.5 rounded-xl border border-theme-border px-4 py-2 text-[12px] font-bold text-theme-fg hover:bg-theme-surface"
                  >
                    <span className="material-icons-outlined text-[16px]">
                      refresh
                    </span>
                    Retry
                  </button>
                </div>
              ) : (
                <div
                  ref={containerRef}
                  className="relative min-h-[9rem] w-full flex-1 overflow-hidden rounded-2xl bg-black"
                >
                  <video
                    ref={videoRef}
                    autoPlay
                    muted
                    // playsInline is required or iOS Safari takes the video
                    // fullscreen the moment it starts.
                    playsInline
                    className="h-full w-full object-cover"
                  />
                  {cameraState === "starting" && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/60">
                      <span className="h-7 w-7 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                    </div>
                  )}

                  <div className="pointer-events-none absolute inset-0">
                    {/* Card guide — everything outside it is masked. Focus is
                        measured inside it; the image sent to the model is the
                        video's full width (captureRect, SGL-594). */}
                    <div
                      className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-xl"
                      style={{
                        width: `${GUIDE_W * 100}%`,
                        aspectRatio: String(CARD_AR),
                        boxShadow: `0 0 0 9999px rgba(0,0,0,0.45), 0 0 ${
                          8 + progress * 22
                        }px ${progress * 6}px ${guideColor}`,
                        transition: "box-shadow 200ms",
                      }}
                    >
                      <div
                        className="absolute inset-0 rounded-xl border"
                        style={{ borderColor: guideColor, opacity: 0.35 }}
                      />
                      {progress < 1 && cameraState === "live" && (
                        <div
                          className="absolute left-0 h-[3px] w-full"
                          style={{
                            background: `linear-gradient(90deg, transparent, ${guideColor}, transparent)`,
                            boxShadow: `0 0 10px ${guideColor}`,
                            animation:
                              "cardscan-sweep 1.9s ease-in-out infinite alternate",
                          }}
                        />
                      )}
                      {progress >= 1 && (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <span className="material-icons-outlined text-[44px] text-white drop-shadow">
                            check_circle
                          </span>
                        </div>
                      )}
                    </div>

                    {cameraState === "live" && (
                      <div className="absolute inset-x-0 bottom-3 flex justify-center">
                        <span
                          role="status"
                          aria-live="polite"
                          className="rounded-full bg-black/55 px-3 py-1.5 text-[11px] font-semibold text-white backdrop-blur-sm"
                        >
                          {atShotLimit
                            ? `${MAX_IMAGES_PER_EXTRACT} cards — that's the limit`
                            : status}
                        </span>
                      </div>
                    )}

                    {flash && (
                      <div
                        className="absolute inset-0 bg-white"
                        style={{
                          animation: "cardscan-flash 280ms ease-out forwards",
                        }}
                      />
                    )}
                  </div>
                </div>
              )}

              {shots.length > 0 && (
                <div className="mt-3 flex flex-none gap-2 overflow-x-auto pb-1">
                  {shots.map((shot, index) => (
                    <div key={shot.id} className="relative flex-shrink-0">
                      <img
                        src={shot.previewUrl}
                        alt={`Card ${index + 1}`}
                        className="h-16 w-28 rounded-lg border border-theme-border object-cover"
                      />
                      <button
                        type="button"
                        onClick={() => removeShot(shot.id)}
                        disabled={busy}
                        className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-theme-fg text-theme-surface disabled:opacity-50"
                        aria-label={`Remove photo ${index + 1}`}
                      >
                        <span className="material-icons-outlined text-[13px]">
                          close
                        </span>
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {error && (
            <p className="px-4 pb-3 text-[12px] font-medium text-theme-danger">
              {error}
            </p>
          )}
        </div>

        {/* Footer — env(safe-area-inset-bottom) keeps the shutter clear of the
            iPhone home indicator. */}
        <div className="border-t border-theme-border px-4 pb-[max(1rem,env(safe-area-inset-bottom))] pt-3">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            // Hints mobile browsers to offer the camera app directly; harmless
            // on desktop, where it just opens the normal file picker.
            capture="environment"
            multiple
            onChange={handleFileInput}
            className="hidden"
          />

          {reviewing ? (
            // Four actions is a lot for one row, so they're grouped and sized
            // down: destructive/navigational on the left, the two that produce
            // something on the right. Wraps rather than squeezing on narrow phones.
            <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-2">
              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  onClick={handleStartOver}
                  disabled={busy}
                  className="rounded-lg px-2.5 py-2 text-[12px] font-medium text-theme-fg-muted hover:bg-theme-background disabled:opacity-60"
                >
                  Start over
                </button>
                <button
                  type="button"
                  onClick={handleScanMore}
                  disabled={busy || atCardLimit}
                  title={
                    atCardLimit
                      ? `That's the ${MAX_IMAGES_PER_EXTRACT}-card limit for one scan`
                      : undefined
                  }
                  className="inline-flex items-center gap-1 rounded-lg border border-theme-border px-2.5 py-2 text-[12px] font-bold text-theme-fg hover:bg-theme-background disabled:opacity-60"
                >
                  <span className="material-icons-outlined text-[15px]">add_a_photo</span>
                  Scan more
                </button>
              </div>

              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  onClick={handleSaveAll}
                  disabled={busy || !unsavedCount}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-theme-primary px-3 py-2 text-[12px] font-bold text-white hover:opacity-90 disabled:opacity-50"
                >
                  {savingAll && (
                    <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                  )}
                  {savingAll
                    ? "Saving…"
                    : unsavedCount === 0
                    ? "All saved"
                    : sendAllWillSend
                    ? `Save & send ${unsavedCount}`
                    : unsavedCount === contacts.length
                    ? "Save all"
                    : `Save ${unsavedCount}`}
                </button>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-between gap-2">
              <div className="flex flex-shrink-0 items-center gap-1">
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={busy || atShotLimit}
                  className="flex h-11 w-11 items-center justify-center rounded-xl border border-theme-border text-theme-fg hover:bg-theme-background disabled:opacity-50"
                  aria-label="Upload a photo instead"
                >
                  <span className="material-icons-outlined text-[20px]">
                    photo_library
                  </span>
                </button>
                {cameraState === "live" && (
                  <button
                    type="button"
                    onClick={() =>
                      setFacingMode((m) =>
                        m === "environment" ? "user" : "environment"
                      )
                    }
                    disabled={busy}
                    className="flex h-11 w-11 items-center justify-center rounded-xl border border-theme-border text-theme-fg hover:bg-theme-background disabled:opacity-50"
                    aria-label="Switch camera"
                    title="Switch camera"
                  >
                    <span className="material-icons-outlined text-[20px]">
                      flip_camera_android
                    </span>
                  </button>
                )}
              </div>

              {cameraState === "live" ? (
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={doCapture}
                    disabled={busy || atShotLimit}
                    className="flex h-14 w-14 items-center justify-center rounded-full border-4 border-theme-border bg-theme-primary text-white transition-transform active:scale-95 disabled:opacity-50"
                    aria-label="Take photo"
                  >
                    <span className="material-icons-outlined text-[24px]">
                      photo_camera
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setAutoEnabled((v) => !v)}
                    aria-pressed={autoEnabled}
                    title="Toggle auto-capture"
                    className={`flex h-11 w-11 items-center justify-center rounded-xl border transition-colors ${
                      autoEnabled
                        ? "border-theme-primary text-theme-primary"
                        : "border-theme-border text-theme-fg-muted"
                    }`}
                    aria-label="Toggle auto-capture"
                  >
                    <span className="material-icons-outlined text-[20px]">
                      bolt
                    </span>
                  </button>
                </div>
              ) : (
                <span className="min-w-0 flex-1 truncate text-center text-[12px] text-theme-fg-muted">
                  {shots.length
                    ? `${shots.length} photo${shots.length > 1 ? "s" : ""} ready`
                    : "Add a photo to continue"}
                </span>
              )}

              {contacts.length > 0 && !shots.length && (
                <button
                  type="button"
                  onClick={() => {
                    stopStream();
                    setMode("review");
                  }}
                  disabled={busy}
                  className="flex-shrink-0 rounded-xl border border-theme-border px-3 text-[12px] font-bold text-theme-fg hover:bg-theme-background disabled:opacity-50"
                  style={{ height: "2.75rem" }}
                >
                  Back to {contacts.length}
                </button>
              )}
              <button
                type="button"
                onClick={handleExtract}
                disabled={busy || !shots.length}
                className="inline-flex h-11 flex-shrink-0 items-center justify-center gap-1.5 rounded-xl bg-theme-primary px-4 text-[13px] font-bold text-white hover:opacity-90 disabled:opacity-50"
              >
                {extracting && (
                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                )}
                {extracting ? "Reading…" : "Read"}
              </button>
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes cardscan-sweep { 0% { top: 4%; } 100% { top: 92%; } }
        @keyframes cardscan-flash { 0% { opacity: 0.9; } 100% { opacity: 0; } }
      `}</style>
    </div>
  );
};
